import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { AuthContextType } from '../types/auth-context.type';
import { UserType } from '../types/user.type';
import { useMutation } from '@apollo/client';
import { LOGIN } from '../services/login.service';
import {
    deleteUserFromStorage,
    getUser,
    saveUserToStorage,
} from '../util/local-storage';
import { TrackingContext } from '../context/TrackingContext';
import { LOGOUT } from '../services/user.service';
import { TrackingEvent } from '../types/tracking-events.enum';

interface Props {
    children: React.ReactNode;
}

const AuthProvider = ({ children }: Props) => {
    const [userState, setUserState] = useState<UserType>();
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>();
    const [login, { loading }] = useMutation<{ login: UserType }>(LOGIN);
    const { setUserProperties } = useContext(TrackingContext);
    const { sendEvent, clearUserProperties, setUserId } = useContext(
        TrackingContext,
    );
    const [logout] = useMutation<boolean>(LOGOUT, {
        onCompleted: () => {
            sendEvent(TrackingEvent.LOGGED_OUT);
            clearUserProperties();
            setUserId(null);
            setUser(undefined);
            setIsAuthenticated(false);
        },
    });

    useEffect(() => {
        const user = getUser();
        if (user) {
            setUserState(user);
            setIsAuthenticated(!!user?.memberId);
        } else {
            setIsAuthenticated(false);
            setUserState(user);
        }
    }, []);

    const setUser = (user?: UserType) => {
        setUserState(user);
        setIsAuthenticated(!!user);
        setUserProperties(user);
        user ? saveUserToStorage(user) : deleteUserFromStorage();
    };

    const contextValue: AuthContextType = {
        isAuthenticated: isAuthenticated,
        setIsAuthenticated,
        isLoading: loading,
        logout,
        login,
        user: userState,
        setUser,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
